import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  CheckCircle2,
  Eye,
  EyeOff,
  Heart,
  Lock,
  Mail,
  Phone,
  ShieldCheck,
  TrendingUp,
  User,
  UserCheck,
  Users,
  X,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom"; // Use useNavigate for page redirection
import { getRequest, postRequest } from "../helpers/api.helper";
import { setSessionItem } from "../helpers/function.helper";
import { useDispatch } from "react-redux";
import { setBureau } from "../store/bureau.slice";
import { clearDashboard } from "../store/dashboard.slice";
import { Capacitor } from "@capacitor/core";
import { getFcmToken } from "../firebase/push-notification.helper";

const BureauLogin = () => {
  const [activeTab, setActiveTab] = useState<"bureau" | "user">("bureau");
  const [selectedRole, setSelectedRole] = useState<"bureau" | "user" | null>(
    null,
  );
  const [showPassword, setShowPassword] = useState(false);
  const [userId, setUserId] = useState(""); // This will hold the email (userId)
  const [password, setPassword] = useState(""); // This will hold the password
  const [error, setError] = useState(""); // To display error messages
  const [rememberMe, setRememberMe] = useState(true);
  const [userLoginId, setUserLoginId] = useState("");
  const [userLoginPassword, setUserLoginPassword] = useState("");
  const [userLoginHint, setUserLoginHint] = useState("");
  const [showUserPassword, setShowUserPassword] = useState(false);
  const [isAutoLoggingIn, setIsAutoLoggingIn] = useState(false);
  const [isLoginSubmitting, setIsLoginSubmitting] = useState(false);
  const [isUserLookupSubmitting, setIsUserLookupSubmitting] = useState(false);
  const [isForgotModalOpen, setIsForgotModalOpen] = useState(false);
  const [forgotStep, setForgotStep] = useState<"request" | "reset">("request");
  const [forgotStatus, setForgotStatus] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [isForgotSubmitting, setIsForgotSubmitting] = useState(false);
  const [isResetSubmitting, setIsResetSubmitting] = useState(false);
  const [forgotForm, setForgotForm] = useState({
    email: "",
    otp: "",
    newPassword: "",
    confirmPassword: "",
  });
  const navigate = useNavigate(); // useNavigate for page redirection
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const desktopLoginBackgroundImage = "/images/bg_login.png";
  const mobileLoginBackgroundImage = "/images/mobile-couple.jpg";
  const bureauSignupPrompt = "Are you a new number?";
  const bureauSignupCta = "Create Matrimony Website";
  const userSignupPrompt = "Are you a new user?";
  const userSignupCta = "Create Profile";
  const isRoleSelected = selectedRole !== null;
  const isUserRoleSelected = activeTab === "user" && isRoleSelected;
  const mobileScrollRef = useRef<HTMLDivElement | null>(null);

  const revealFocusedField = useCallback((target: EventTarget | null) => {
    if (typeof window === "undefined" || window.innerWidth >= 1024) {
      return;
    }

    if (!(target instanceof HTMLElement)) {
      return;
    }

    const field = target.closest("input, textarea, select, button");
    if (!(field instanceof HTMLElement)) {
      return;
    }

    const nearestScrollContainer =
      field.closest<HTMLElement>("[data-scroll-container='true']") ??
      mobileScrollRef.current;
    if (!nearestScrollContainer) {
      return;
    }

    const containerRect = nearestScrollContainer.getBoundingClientRect();
    const fieldRect = field.getBoundingClientRect();
    const topPadding = 20;
    const bottomPadding = 140;

    if (fieldRect.bottom > containerRect.bottom - bottomPadding) {
      nearestScrollContainer.scrollBy({
        top: fieldRect.bottom - containerRect.bottom + bottomPadding,
        behavior: "smooth",
      });
      return;
    }

    if (fieldRect.top < containerRect.top + topPadding) {
      nearestScrollContainer.scrollBy({
        top: fieldRect.top - containerRect.top - topPadding,
        behavior: "smooth",
      });
    }
  }, []);

  useEffect(() => {
    const htmlElement = document.documentElement;
    const bodyElement = document.body;
    const rootElement = document.getElementById("root");

    htmlElement.classList.add("login-page-lock");
    bodyElement.classList.add("login-page-lock");
    rootElement?.classList.add("login-page-lock");

    return () => {
      htmlElement.classList.remove("login-page-lock");
      bodyElement.classList.remove("login-page-lock");
      rootElement?.classList.remove("login-page-lock");
    };
  }, []);

  const highlights = [
    {
      icon: Users,
      title: "Manage Profiles Efficiently",
      description:
        "Track enquiries, matches, and member activity from one bureau workspace.",
    },
    {
      icon: Heart,
      title: "Smart Matching Solutions",
      description:
        "Move faster with a focused dashboard built for relationship-led sales teams.",
    },
    {
      icon: TrendingUp,
      title: "Grow Your Bureau Business",
      description:
        "Turn daily operations into a polished client experience that builds trust.",
    },
  ];
  const roleOptions = [
    {
      key: "bureau" as const,
      eyebrow: "I am",
      title: "Bureau Owner",
      description: "Manage bureau profiles, enquiries, matches, and members.",
      icon: ShieldCheck,
      iconClassName:
        "bg-[linear-gradient(180deg,#F032A4_0%,#6D28D9_100%)] text-white shadow-[0_18px_40px_rgba(109,40,217,0.22)]",
    },
    {
      key: "user" as const,
      eyebrow: "I am",
      title: "User",
      description: "Login or create your profile to find suitable matches.",
      icon: UserCheck,
      iconClassName:
        "bg-[linear-gradient(180deg,#C084FC_0%,#6D28D9_100%)] text-white shadow-[0_18px_40px_rgba(109,40,217,0.18)]",
    },
  ];

  const normalizeEmail = (value: string) => value.trim().toLowerCase();

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleUserPasswordVisibility = () => {
    setShowUserPassword(!showUserPassword);
  };

  const syncFcmToken = async (bureauId: string) => {
    try {
      const platform = Capacitor.getPlatform();
      if (platform === "web") {
        return;
      }

      const token = await getFcmToken();

      if (!token) {
        console.warn("Skipping FCM token sync because no token was returned.");
        return;
      }
      await postRequest("/firebase/save-token", {
        userId: bureauId,
        token,
        platform,
        userType: "bureau",
      });
    } catch (tokenError) {
      console.error("Failed to sync FCM token", tokenError);
    }
  };

  const handleLogin = async (e: any) => {
    e.preventDefault();
    if (isLoginSubmitting || isAutoLoggingIn) return;

    setError("");
    setIsLoginSubmitting(true);

    try {
      const response = await postRequest("/bureau/login", {
        email: userId,
        password: password,
      });
      // Check if login was successful
      if (response.success) {
        dispatch(clearDashboard());
        await Promise.all([
          setSessionItem("sessionToken", response.data.token),
          setSessionItem("bureauData", response.data.bureau),
        ]);
        dispatch(setBureau(response.data.bureau));
        if (response.data?.bureau?.id) {
          await syncFcmToken(response.data.bureau.id);
        }
        navigate("/admin/dashboard");
      } else {
        // Set the error message if login failed
        setError(response.message || "Login failed");
      }
    } catch (err) {
      console.error("Login failed", err);
    } finally {
      setIsLoginSubmitting(false);
    }
  };

  const handleUserLoginContinue = async (
    e: React.FormEvent<HTMLFormElement>,
  ) => {
    e.preventDefault();

    if (isUserLookupSubmitting) return;

    const normalizedValue = userLoginId.trim();
    if (!normalizedValue) {
      setUserLoginHint("Enter a mobile number or marital ID to continue.");
      return;
    }

    if (!userLoginPassword.trim()) {
      setUserLoginHint("Enter your password to continue.");
      return;
    }

    setIsUserLookupSubmitting(true);
    setUserLoginHint("");

    try {
      const response = await getRequest(
        `/common/user/lookup/${encodeURIComponent(normalizedValue)}`,
      );

      if (!response.success || !response.data?.maritalId) {
        setUserLoginHint(
          response.message ||
            "No user profile was found for the provided mobile number or marital ID.",
        );
        return;
      }

      const redirectUrl = `https://meemarriage.com/login?martialId=${encodeURIComponent(
        response.data.maritalId,
      )}&password=${encodeURIComponent(userLoginPassword.trim())}`;
      window.location.href = redirectUrl;
    } catch (err) {
      setUserLoginHint(
        "Unable to validate the user profile right now. Please try again.",
      );
    } finally {
      setIsUserLookupSubmitting(false);
    }
  };

  const openForgotPasswordModal = () => {
    setForgotForm({
      email: userId,
      otp: "",
      newPassword: "",
      confirmPassword: "",
    });
    setForgotStatus(null);
    setForgotStep("request");
    setIsForgotModalOpen(true);
  };

  const closeForgotPasswordModal = () => {
    if (isForgotSubmitting || isResetSubmitting) return;
    setIsForgotModalOpen(false);
    setForgotForm({
      email: "",
      otp: "",
      newPassword: "",
      confirmPassword: "",
    });
    setForgotStep("request");
    setForgotStatus(null);
  };

  const handleForgotInputChange = (
    field: keyof typeof forgotForm,
    value: string,
  ) => {
    setForgotForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleForgotPasswordSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    const normalizedEmail = normalizeEmail(forgotForm.email || "");
    if (!normalizedEmail) {
      setForgotStatus({
        type: "error",
        text: "Please enter your registered email address.",
      });
      return;
    }

    setIsForgotSubmitting(true);
    setForgotStatus(null);

    try {
      const response = await postRequest("/bureau/password/forgot", {
        email: normalizedEmail,
      });

      if (response.success) {
        setForgotStatus({
          type: "success",
          text:
            response.message ||
            "OTP sent successfully. Please check your email for the code.",
        });
        setForgotStep("reset");
        setForgotForm((prev) => ({ ...prev, email: normalizedEmail }));
      } else {
        setForgotStatus({
          type: "error",
          text: response.message || "Unable to send OTP. Please try again.",
        });
      }
    } catch (err) {
      setForgotStatus({
        type: "error",
        text: "An unexpected error occurred. Please try again later.",
      });
    } finally {
      setIsForgotSubmitting(false);
    }
  };

  const handleResetPasswordSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    const normalizedEmail = normalizeEmail(forgotForm.email || "");
    if (!normalizedEmail) {
      setForgotStatus({
        type: "error",
        text: "Email is required to reset the password.",
      });
      return;
    }

    if (!forgotForm.otp.trim()) {
      setForgotStatus({
        type: "error",
        text: "Please enter the OTP sent to your email.",
      });
      return;
    }

    if (!forgotForm.newPassword.trim()) {
      setForgotStatus({
        type: "error",
        text: "New password cannot be empty.",
      });
      return;
    }

    if (forgotForm.newPassword !== forgotForm.confirmPassword) {
      setForgotStatus({
        type: "error",
        text: "New password and confirm password do not match.",
      });
      return;
    }

    setIsResetSubmitting(true);
    setForgotStatus(null);

    try {
      const response = await postRequest("/bureau/password/reset", {
        email: normalizedEmail,
        otp: forgotForm.otp.trim(),
        newPassword: forgotForm.newPassword.trim(),
      });

      if (response.success) {
        setForgotStatus({
          type: "success",
          text:
            response.message ||
            "Password reset successfully. You can now log in with the new password.",
        });
        setUserId(normalizedEmail);
        setPassword(forgotForm.newPassword.trim());
      } else {
        setForgotStatus({
          type: "error",
          text:
            response.message || "Unable to reset password. Please try again.",
        });
      }
    } catch (err) {
      setForgotStatus({
        type: "error",
        text: "An unexpected error occurred. Please try again later.",
      });
    } finally {
      setIsResetSubmitting(false);
    }
  };

  return (
    <>
      {(isLoginSubmitting || isAutoLoggingIn) && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/45 backdrop-blur-sm">
          <div className="flex min-w-[220px] flex-col items-center gap-4 rounded-[28px] bg-white px-8 py-7 shadow-[0_28px_80px_rgba(15,23,42,0.20)]">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-violet-100 border-t-[#6D28D9]" />
            <div className="text-center">
              <p className="text-base font-bold text-slate-900">Logging in</p>
              <p className="mt-1 text-sm text-slate-500">
                Please wait while we open your dashboard
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="relative min-h-[100dvh] overflow-x-hidden bg-[#FFF1F7] lg:h-[var(--app-height)] lg:overflow-hidden lg:bg-[radial-gradient(circle_at_top_left,_#ffffff_0%,_#fff7fb_35%,_#fde7f1_100%)]">
        <div className="pointer-events-none absolute inset-0 hidden overflow-hidden lg:block">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${desktopLoginBackgroundImage})` }}
          />
        </div>

        <div className="relative mx-auto flex min-h-[100dvh] w-full max-w-[1500px] flex-col lg:min-h-0 lg:h-full lg:grid lg:grid-cols-[1.03fr_0.87fr]">
          <section className="relative hidden min-h-[560px] overflow-hidden lg:block lg:min-h-0">
            <div className="relative z-10 flex min-h-[560px] flex-col justify-start px-6 pb-0 pt-10 sm:px-8 sm:pt-12 lg:h-full lg:justify-between lg:overflow-hidden lg:px-8 lg:pb-10 xl:px-10 xl:pb-12 xl:pt-14">
              <div className="mt-0 max-w-[28rem] sm:mt-2 xl:mt-12">
                <div className="mb-12 flex items-center gap-3">
                  <div className="flex h-14 w-14 items-center justify-center">
                    <img
                      src="/logo/1024.png"
                      alt="Matrimony Studio logo"
                      className="h-12 w-12 object-contain"
                    />
                  </div>
                  <div>
                    <p className="text-[26px] font-extrabold leading-none text-[#5E1E9B]">
                      Matrimony Studio
                    </p>
                    <p className="mt-1 text-[15px] font-medium leading-snug text-[#4B5563]">
                      Bureau Portal
                    </p>
                  </div>
                </div>

                <h1 className="bureau-display mt-12 max-w-[240px] text-[34px] font-bold leading-[1.08] tracking-[-0.03em] text-[#5E1E9B] sm:max-w-[280px] sm:text-[38px] lg:mt-4 lg:max-w-none xl:text-[42px]">
                  Connecting Hearts,
                  <br />
                  Building Happiness
                </h1>
                <p className="mt-6 max-w-[280px] text-[21px] font-normal leading-[1.55] text-[#374151] lg:text-[18px] lg:leading-[1.65]">
                  A complete matrimonial management solution for modern bureaus.
                </p>

                <div className="mt-10 hidden max-w-[17rem] space-y-7 text-[#374151] lg:block">
                  {highlights.map(({ icon: Icon, title }) => (
                    <div key={title} className="flex items-center gap-4">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] border border-[#F2D3E7] bg-[#FFF4F9] text-[#A21CAF] shadow-sm">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-[16px] font-medium leading-[1.35] text-[#374151]">
                          {title}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative hidden items-start gap-5 text-white lg:flex">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/14">
                  <ShieldCheck className="h-6 w-6" />
                </div>
                <div>
                  <p className="text-[17px] font-bold leading-[1.2] text-white">
                    Secure • Reliable • Trusted
                  </p>
                  <p className="mt-2 max-w-[14rem] text-[16px] leading-[1.45] text-white/90">
                    Empowering thousands of matrimonial bureaus
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="relative z-10 min-h-full px-0 py-0 lg:z-20 lg:flex lg:min-h-0 lg:items-center lg:justify-center lg:bg-transparent lg:px-6 lg:py-0 xl:px-8">
            <div className="relative flex min-h-[100dvh] w-full flex-col lg:hidden">
              <div
                className={`pointer-events-none absolute inset-x-0 top-0 overflow-hidden ${
                  isRoleSelected ? "h-[300px]" : "h-[360px]"
                }`}
              >
                <div
                  className="absolute inset-0 bg-cover bg-no-repeat"
                  style={{
                    backgroundImage: `url(${mobileLoginBackgroundImage})`,
                    backgroundPosition: "50% 26%",
                  }}
                />

                <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.02)_0%,rgba(255,255,255,0.02)_32%,rgba(95,15,70,0.18)_55%,rgba(90,10,75,0.72)_82%,rgba(83,16,92,0.94)_100%)]" />
                <div className="absolute inset-x-0 bottom-0 h-24 bg-[linear-gradient(180deg,rgba(91,18,92,0)_0%,rgba(91,18,92,0.24)_45%,rgba(255,248,252,0.18)_100%)]" />
              </div>

              <div
                className={`relative flex flex-col px-5 pt-[max(10px,env(safe-area-inset-top))] ${
                  isRoleSelected ? "min-h-[300px] pb-2" : "min-h-[360px] pb-4"
                }`}
              >
                <div className="flex w-fit items-center gap-2">
                  <div className="flex h-11 w-11 items-center justify-center">
                    <img
                      src="/logo/1024.png"
                      alt="Matrimony Studio logo"
                      className="h-11 w-11 object-contain"
                    />
                  </div>
                  <div>
                    <p className="text-[17px] font-extrabold leading-none text-[#4C1D95]">
                      Matrimony Studio
                    </p>
                    <p className="mt-1 text-[13px] font-medium leading-none text-[#374151]">
                      Bureau Portal
                    </p>
                  </div>
                </div>

                <div
                  className={`${isRoleSelected ? "mt-auto pb-0" : "mt-auto pb-2"}`}
                >
                  <h1
                    className={`bureau-display max-w-[300px] font-bold leading-[1.06] tracking-[-0.035em] text-white drop-shadow-[0_3px_16px_rgba(55,0,30,0.32)] ${
                      isRoleSelected ? "text-[24px]" : "text-[28px]"
                    }`}
                  >
                    Connecting Hearts,
                    <br />
                    Building Happiness
                  </h1>

                  <div
                    className={`flex max-w-[280px] items-center gap-3 ${
                      isRoleSelected ? "mt-2" : "mt-3"
                    }`}
                  >
                    <div className="h-px flex-1 bg-white/65" />
                    <Heart className="h-4 w-4 fill-pink-300 text-pink-300" />
                    <div className="h-px flex-1 bg-white/65" />
                  </div>

                  <p
                    className={`max-w-[295px] text-white ${
                      isRoleSelected
                        ? "mt-2 text-[12px] leading-[1.35]"
                        : "mt-2.5 text-[14px] leading-[1.45]"
                    }`}
                  >
                    A complete matrimonial management solution for modern
                    bureaus.
                  </p>
                </div>
              </div>

              <div
                ref={mobileScrollRef}
                data-scroll-container="true"
                className={`relative z-10 -mt-[28px] flex flex-1 flex-col overflow-y-auto rounded-t-[40px] bg-[linear-gradient(180deg,#FFFFFF_0%,#FFF8FC_58%,#FFF3F8_100%)] px-5 pt-3 shadow-[0_-16px_45px_rgba(92,28,93,0.14)] ${
                  isRoleSelected
                    ? "pb-[max(20px,env(safe-area-inset-bottom))]"
                    : "pb-[max(24px,env(safe-area-inset-bottom))]"
                }`}
              >
                <div className="mx-auto mb-2 h-[5px] w-[54px] rounded-full bg-gradient-to-r from-pink-100 via-pink-300 to-pink-100" />

                {isAutoLoggingIn && (
                  <div className="mt-5 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-center text-sm font-medium text-blue-700">
                    {searchParams.get("source") === "admin"
                      ? "Admin panel auto-login in progress. Please wait."
                      : "Auto-login in progress. Please wait."}
                  </div>
                )}

                {selectedRole === null ? (
                  <>
                    <div className="mt-1 text-center">
                      <h2 className="text-[23px] font-extrabold leading-tight text-[#1F2937]">
                        Continue as
                      </h2>
                      <div className="mt-2 flex items-center justify-center gap-2 text-pink-300">
                        <span className="text-lg leading-none">∞</span>
                        <div className="h-px w-12 bg-pink-200" />
                        <Heart className="h-4 w-4" />
                        <div className="h-px w-12 bg-pink-200" />
                        <span className="text-lg leading-none">∞</span>
                      </div>
                    </div>

                    <div className="mt-2.5">
                      {roleOptions.map(
                        ({
                          key,
                          eyebrow,
                          title,
                          description,
                          icon: Icon,
                          iconClassName,
                        }) => (
                          <React.Fragment key={key}>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedRole(key);
                                setActiveTab(key);
                                setError("");
                                setUserLoginHint("");
                              }}
                              className="grid min-h-[82px] w-full grid-cols-[54px_minmax(0,1fr)_38px] items-center gap-3 rounded-[22px] border border-[#F7B9DC] bg-white/95 px-4 py-2.5 text-left shadow-[0_9px_24px_rgba(219,39,119,0.09)] transition active:scale-[0.985]"
                            >
                              <div
                                className={`flex h-[54px] w-[54px] items-center justify-center rounded-[18px] ${iconClassName}`}
                              >
                                <Icon className="h-7 w-7" />
                              </div>
                              <div className="min-w-0">
                                <p className="text-[13px] font-bold leading-none text-[#1F2937]">
                                  {eyebrow}
                                </p>
                                <p
                                  className={`mt-1 whitespace-nowrap text-[18px] font-extrabold leading-none ${
                                    key === "bureau"
                                      ? "text-[#C2188F]"
                                      : "text-[#6D28D9]"
                                  }`}
                                >
                                  {title}
                                </p>
                                <p className="mt-1 line-clamp-2 max-w-[190px] overflow-hidden text-[11px] leading-[15px] text-[#4B5563]">
                                  {description}
                                </p>
                              </div>
                              <div className="flex h-[38px] w-[38px] items-center justify-center rounded-full bg-[#FFF1F7] text-[#C2188F]">
                                <ArrowRight className="h-5 w-5" />
                              </div>
                            </button>

                            {key === "bureau" && (
                              <div className="my-1.5 flex items-center gap-3 px-10">
                                <div className="h-px flex-1 bg-pink-200" />
                                <span className="text-[15px] font-extrabold text-pink-400">
                                  OR
                                </span>
                                <div className="h-px flex-1 bg-pink-200" />
                              </div>
                            )}
                          </React.Fragment>
                        ),
                      )}
                    </div>

                    <div className="mt-2 flex h-[50px] items-center gap-3 rounded-[18px] bg-[linear-gradient(90deg,#FFF1F7_0%,#F5F3FF_100%)] px-4 text-[#5B21B6]">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white">
                        <ShieldCheck className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <p className="whitespace-nowrap text-[12px] font-extrabold leading-tight">
                          Secure • Reliable • Trusted
                        </p>
                        <p className="mt-0.5 whitespace-nowrap text-[10px] leading-tight text-slate-600">
                          Empowering thousands of matrimonial bureaus
                        </p>
                      </div>
                    </div>
                  </>
                ) : activeTab === "bureau" ? (
                  <form
                    onSubmit={handleLogin}
                    onFocusCapture={(e) => revealFocusedField(e.target)}
                    className={`mt-3 flex min-h-0 flex-1 flex-col ${
                      isUserRoleSelected ? "space-y-2" : "space-y-2.5"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedRole(null);
                        setUserLoginHint("");
                        setUserLoginPassword("");
                        setError("");
                      }}
                      className="text-sm font-semibold text-[#6D28D9]"
                    >
                      Back
                    </button>
                    <div>
                      <label
                        htmlFor="userId"
                        className="mb-1 block text-[13px] font-bold text-[#374151]"
                      >
                        Mobile Number
                      </label>
                      <div className="flex h-[48px] items-center rounded-2xl border border-[#E9D5FF] bg-white px-4 shadow-sm transition focus-within:border-[#6D28D9] focus-within:ring-4 focus-within:ring-violet-100">
                        <Phone className="h-5 w-5 shrink-0 text-[#9CA3AF]" />
                        <input
                          type="tel"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          id="userId"
                          value={userId}
                          onChange={(e) => setUserId(e.target.value)}
                          className="h-full w-full border-0 bg-transparent px-3 text-[14px] text-[#1F2937] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-0 autofill:shadow-[inset_0_0_0px_1000px_white] autofill:[-webkit-text-fill-color:#1F2937] lg:autofill:shadow-[inset_0_0_0px_1000px_white]"
                          placeholder="Enter your mobile number"
                          required
                          disabled={isAutoLoggingIn || isLoginSubmitting}
                        />
                      </div>
                    </div>

                    <div className="relative">
                      <label
                        htmlFor="password"
                        className="mb-1 block text-[13px] font-bold text-[#374151]"
                      >
                        Password
                      </label>
                      <div className="flex h-[48px] items-center rounded-2xl border border-[#E9D5FF] bg-white px-4 shadow-sm transition focus-within:border-[#6D28D9] focus-within:ring-4 focus-within:ring-violet-100">
                        <Lock className="h-5 w-5 shrink-0 text-[#9CA3AF]" />
                        <input
                          type={showPassword ? "text" : "password"}
                          id="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="h-full w-full border-0 bg-transparent px-3 pr-2 text-[14px] text-[#1F2937] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-0 autofill:shadow-[inset_0_0_0px_1000px_white] autofill:[-webkit-text-fill-color:#1F2937] lg:autofill:shadow-[inset_0_0_0px_1000px_white]"
                          placeholder="Enter your password"
                          required
                          disabled={isAutoLoggingIn || isLoginSubmitting}
                        />
                        <button
                          type="button"
                          onClick={togglePasswordVisibility}
                          className="text-[#6B7280]"
                          disabled={isAutoLoggingIn || isLoginSubmitting}
                        >
                          {showPassword ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    {error && (
                      <div
                        className={`rounded-2xl px-4 py-3 text-sm ${
                          error.includes("suspended")
                            ? "border border-red-300 bg-red-50 text-red-700"
                            : "border border-rose-200 bg-rose-50 text-rose-600"
                        }`}
                        dangerouslySetInnerHTML={{ __html: error }}
                      />
                    )}

                    <div className="flex items-center justify-between gap-3 text-sm">
                      <label className="flex cursor-pointer items-center gap-2.5 text-[14px] text-[#6B7280]">
                        <input
                          type="checkbox"
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          className="h-4 w-4 rounded border-slate-300 text-[#6D28D9] focus:ring-violet-500"
                        />
                        <span>Remember me</span>
                      </label>
                      <button
                        type="button"
                        onClick={openForgotPasswordModal}
                        className="text-[14px] font-semibold text-[#6D28D9]"
                        disabled={isAutoLoggingIn || isLoginSubmitting}
                      >
                        Forgot Password?
                      </button>
                    </div>

                    <button
                      type="submit"
                      className="flex h-[50px] w-full items-center justify-center gap-3 rounded-2xl bg-gradient-to-r from-[#6D28D9] via-[#9333EA] to-[#DB2777] px-5 text-[17px] font-extrabold text-white shadow-[0_18px_40px_rgba(109,40,217,0.34)] disabled:cursor-not-allowed disabled:opacity-60"
                      disabled={isAutoLoggingIn || isLoginSubmitting}
                    >
                      <span>
                        {isAutoLoggingIn || isLoginSubmitting
                          ? "Loading..."
                          : "Login"}
                      </span>
                      <ArrowRight className="h-5 w-5" />
                    </button>

                    <div className="mt-auto space-y-2.5 pt-1 text-center">
                      <p className="text-[14px] text-[#6B7280]">
                        {bureauSignupPrompt}
                      </p>
                      <button
                        type="button"
                        onClick={() =>
                          window.open(
                            "https://riyatechmatrimony.com/creatematrimonyapp/website",
                            "_blank",
                          )
                        }
                        className="flex h-[50px] w-full items-center justify-center gap-3 rounded-2xl bg-gradient-to-r from-[#0F9D7A] via-[#10B981] to-[#34D399] px-5 text-[17px] font-extrabold text-white shadow-[0_18px_40px_rgba(16,185,129,0.28)] disabled:cursor-not-allowed disabled:opacity-60"
                        disabled={isAutoLoggingIn || isLoginSubmitting}
                      >
                        {bureauSignupCta}
                      </button>
                    </div>
                  </form>
                ) : (
                  <form
                    onSubmit={handleUserLoginContinue}
                    onFocusCapture={(e) => revealFocusedField(e.target)}
                    className="mt-3 flex min-h-0 flex-1 flex-col space-y-2"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedRole(null);
                        setUserLoginHint("");
                        setUserLoginPassword("");
                        setError("");
                      }}
                      className="text-sm font-semibold text-[#6D28D9]"
                    >
                      Back
                    </button>
                    <div>
                      <label
                        htmlFor="userLoginId"
                        className="mb-1 block text-[13px] font-bold text-[#374151]"
                      >
                        Mobile Number / Marital ID
                      </label>
                      <div className="flex h-[48px] items-center rounded-2xl border border-[#E9D5FF] bg-white px-4 shadow-sm transition focus-within:border-[#6D28D9] focus-within:ring-4 focus-within:ring-violet-100">
                        <UserCheck className="h-5 w-5 shrink-0 text-[#6B7280]" />
                        <input
                          type="text"
                          id="userLoginId"
                          value={userLoginId}
                          onChange={(e) => {
                            setUserLoginId(e.target.value);
                            setUserLoginHint("");
                          }}
                          className="h-full w-full border-0 bg-transparent px-3 text-[14px] text-[#1F2937] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-0"
                          placeholder="Enter mobile number or marital ID"
                          disabled={isUserLookupSubmitting}
                        />
                      </div>
                    </div>

                    <div className="relative">
                      <label
                        htmlFor="userLoginPassword"
                        className="mb-1 block text-[13px] font-bold text-[#374151]"
                      >
                        Password
                      </label>
                      <div className="flex h-[48px] items-center rounded-2xl border border-[#E9D5FF] bg-white px-4 shadow-sm transition focus-within:border-[#6D28D9] focus-within:ring-4 focus-within:ring-violet-100">
                        <Lock className="h-5 w-5 shrink-0 text-[#9CA3AF]" />
                        <input
                          type={showUserPassword ? "text" : "password"}
                          id="userLoginPassword"
                          value={userLoginPassword}
                          onChange={(e) => {
                            setUserLoginPassword(e.target.value);
                            setUserLoginHint("");
                          }}
                          className="h-full w-full border-0 bg-transparent px-3 pr-2 text-[14px] text-[#1F2937] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-0"
                          placeholder="Enter your password"
                          disabled={isUserLookupSubmitting}
                        />
                        <button
                          type="button"
                          onClick={toggleUserPasswordVisibility}
                          className="text-[#6B7280]"
                          disabled={isUserLookupSubmitting}
                        >
                          {showUserPassword ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    </div>

                    {userLoginHint && (
                      <div className="rounded-2xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm font-medium text-violet-700">
                        {userLoginHint}
                      </div>
                    )}

                    <button
                      type="submit"
                      className="flex h-[50px] w-full items-center justify-center gap-3 rounded-2xl bg-gradient-to-r from-[#6D28D9] via-[#9333EA] to-[#DB2777] px-5 text-[17px] font-extrabold text-white shadow-[0_18px_40px_rgba(109,40,217,0.34)] disabled:cursor-not-allowed disabled:opacity-60"
                      disabled={isUserLookupSubmitting}
                    >
                      <span>
                        {isUserLookupSubmitting ? "Checking..." : "Continue"}
                      </span>
                      <ArrowRight className="h-5 w-5" />
                    </button>

                    <div className="mt-auto space-y-2.5 pt-1 text-center">
                      <p className="text-[14px] text-[#6B7280]">
                        {userSignupPrompt}
                      </p>
                      <button
                        type="button"
                        onClick={() => navigate("/profile/register")}
                        className="flex h-[50px] w-full items-center justify-center gap-3 rounded-2xl bg-gradient-to-r from-[#0F9D7A] via-[#10B981] to-[#34D399] px-5 text-[17px] font-extrabold text-white shadow-[0_18px_40px_rgba(16,185,129,0.28)] disabled:cursor-not-allowed disabled:opacity-60"
                        disabled={isUserLookupSubmitting}
                      >
                        {userSignupCta}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>

            <div className="hidden w-full max-w-[390px] rounded-[28px] border border-white/70 bg-white/94 px-5 py-5 shadow-[0_24px_70px_rgba(88,28,135,0.20)] backdrop-blur-md sm:px-6 sm:py-6 lg:block lg:max-w-[580px] lg:rounded-[30px] lg:border-0 lg:bg-white lg:p-8 lg:px-11 lg:py-10 lg:shadow-[0_26px_80px_rgba(15,23,42,0.12)] lg:backdrop-blur-0">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border-4 border-[#FCE7F3] lg:h-[74px] lg:w-[74px]">
                <img
                  src="/logo/1024.png"
                  alt="Matrimony Studio logo"
                  className="h-10 w-10 object-contain lg:h-12 lg:w-12"
                />
              </div>

              <div className="mt-4 text-center lg:mt-5">
                <h2 className="text-[28px] font-extrabold leading-tight tracking-[-0.02em] text-[#6D28D9] lg:text-[34px] lg:leading-[1.1]">
                  Matrimony Studio
                </h2>
                {selectedRole !== null && (
                  <p className="mt-1.5 text-[22px] font-bold leading-tight text-[#1F2937] lg:mt-2 lg:text-[30px] lg:leading-[1.15]">
                    {activeTab === "bureau" ? "Bureau Login" : "User Login"}
                  </p>
                )}
                <div className="mt-4 flex items-center justify-center gap-3 text-pink-400 lg:mt-5 lg:gap-4">
                  <div className="h-px w-24 bg-[#E9D5FF]" />
                  <Heart className="h-5 w-5" />
                  <div className="h-px w-24 bg-[#E9D5FF]" />
                </div>
              </div>

              {isAutoLoggingIn && (
                <div className="mt-5 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-center text-sm font-medium text-blue-700">
                  {searchParams.get("source") === "admin"
                    ? "Admin panel auto-login in progress. Please wait."
                    : "Auto-login in progress. Please wait."}
                </div>
              )}

              {selectedRole === null ? (
                <div className="mt-6 space-y-4 lg:mt-8">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedRole("bureau");
                      setActiveTab("bureau");
                      setUserLoginHint("");
                    }}
                    className="flex w-full items-center justify-between rounded-2xl border border-pink-100 bg-gradient-to-r from-white to-pink-50 px-4 py-4 text-left shadow-sm transition hover:shadow-md"
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#FCE7F3] to-[#F3E8FF] text-[#6D28D9]">
                        <ShieldCheck className="h-6 w-6" />
                      </div>
                      <div>
                        <p className="text-[18px] font-bold text-[#1F2937]">
                          Bureau Owner
                        </p>
                        <p className="mt-1 max-w-[15rem] text-[13px] leading-5 text-[#6B7280] lg:max-w-[20rem] lg:text-sm">
                          Manage bureau profiles, enquiries, matches, and
                          members.
                        </p>
                      </div>
                    </div>
                    <ArrowRight className="h-5 w-5 shrink-0 text-[#DB2777]" />
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setSelectedRole("user");
                      setActiveTab("user");
                      setError("");
                    }}
                    className="flex w-full items-center justify-between rounded-2xl border border-pink-100 bg-gradient-to-r from-white to-pink-50 px-4 py-4 text-left shadow-sm transition hover:shadow-md"
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-[#FCE7F3] to-[#F3E8FF] text-[#6D28D9]">
                        <UserCheck className="h-6 w-6" />
                      </div>
                      <div>
                        <p className="text-[18px] font-bold text-[#1F2937]">
                          User
                        </p>
                        <p className="mt-1 max-w-[15rem] text-[13px] leading-5 text-[#6B7280] lg:max-w-[20rem] lg:text-sm">
                          Login or create your profile to find suitable matches.
                        </p>
                      </div>
                    </div>
                    <ArrowRight className="h-5 w-5 shrink-0 text-[#DB2777]" />
                  </button>
                </div>
              ) : activeTab === "bureau" ? (
                <form
                  onSubmit={handleLogin}
                  className="mt-5 space-y-4 lg:mt-7 lg:space-y-[22px]"
                >
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedRole(null);
                      setUserLoginHint("");
                      setUserLoginPassword("");
                      setError("");
                    }}
                    className="text-sm font-semibold text-[#6D28D9] transition hover:text-[#DB2777]"
                  >
                    Back
                  </button>
                  <div>
                    <label
                      htmlFor="userId"
                      className="mb-1.5 block text-[14px] font-bold text-[#374151] lg:mb-2 lg:text-[16px]"
                    >
                      Mobile Number
                    </label>
                    <div className="flex h-[52px] items-center rounded-xl border border-[#E5E7EB] bg-white px-4 shadow-sm transition focus-within:border-[#6D28D9] focus-within:ring-4 focus-within:ring-violet-100 lg:h-[60px] lg:rounded-[14px]">
                      <Phone className="h-5 w-5 shrink-0 text-[#9CA3AF] lg:h-[22px] lg:w-[22px]" />
                      <input
                        type="tel"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        id="userId"
                        value={userId}
                        onChange={(e) => setUserId(e.target.value)}
                        className="h-full w-full border-0 bg-transparent px-3 text-[15px] text-[#1F2937] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-0 autofill:shadow-[inset_0_0_0px_1000px_white] autofill:[-webkit-text-fill-color:#1F2937] lg:text-[18px] lg:autofill:shadow-[inset_0_0_0px_1000px_white]"
                        placeholder="Enter your mobile number"
                        required
                        disabled={isAutoLoggingIn || isLoginSubmitting}
                      />
                    </div>
                  </div>

                  <div className="relative">
                    <label
                      htmlFor="password"
                      className="mb-1.5 block text-[14px] font-bold text-[#374151] lg:mb-2 lg:text-[16px]"
                    >
                      Password
                    </label>
                    <div className="flex h-[52px] items-center rounded-xl border border-[#E5E7EB] bg-white px-4 shadow-sm transition focus-within:border-[#6D28D9] focus-within:ring-4 focus-within:ring-violet-100 lg:h-[60px] lg:rounded-[14px]">
                      <Lock className="h-5 w-5 shrink-0 text-[#9CA3AF] lg:h-[22px] lg:w-[22px]" />
                      <input
                        type={showPassword ? "text" : "password"}
                        id="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="h-full w-full border-0 bg-transparent px-3 pr-2 text-[15px] text-[#1F2937] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-0 autofill:shadow-[inset_0_0_0px_1000px_white] autofill:[-webkit-text-fill-color:#1F2937] lg:text-[18px] lg:autofill:shadow-[inset_0_0_0px_1000px_white]"
                        placeholder="Enter your password"
                        required
                        disabled={isAutoLoggingIn || isLoginSubmitting}
                      />
                      <button
                        type="button"
                        onClick={togglePasswordVisibility}
                        className="text-[#6B7280] transition hover:text-[#6D28D9]"
                        disabled={isAutoLoggingIn || isLoginSubmitting}
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5 lg:h-[22px] lg:w-[22px]" />
                        ) : (
                          <Eye className="h-5 w-5 lg:h-[22px] lg:w-[22px]" />
                        )}
                      </button>
                    </div>
                  </div>

                  {error && (
                    <div
                      className={`rounded-2xl px-4 py-3 text-sm ${
                        error.includes("suspended")
                          ? "border border-red-300 bg-red-50 text-red-700"
                          : "border border-rose-200 bg-rose-50 text-rose-600"
                      }`}
                      dangerouslySetInnerHTML={{ __html: error }}
                    />
                  )}

                  <div className="flex items-center justify-between gap-3 text-sm">
                    <label className="flex cursor-pointer items-center gap-2.5 text-[14px] text-[#6B7280] lg:gap-3 lg:text-[16px]">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="h-4 w-4 rounded border-slate-300 text-[#6D28D9] focus:ring-violet-500 lg:h-5 lg:w-5"
                      />
                      <span>Remember me</span>
                    </label>
                    <button
                      type="button"
                      onClick={openForgotPasswordModal}
                      className="text-[14px] font-semibold text-[#6D28D9] transition hover:text-[#DB2777] lg:text-[16px]"
                      disabled={isAutoLoggingIn || isLoginSubmitting}
                    >
                      Forgot Password?
                    </button>
                  </div>

                  <button
                    type="submit"
                    className="flex h-[52px] w-full items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-[#6D28D9] via-[#9333EA] to-[#DB2777] px-5 text-[18px] font-extrabold text-white shadow-[0_18px_40px_rgba(109,40,217,0.34)] transition hover:translate-y-[-1px] hover:shadow-[0_22px_50px_rgba(109,40,217,0.40)] disabled:cursor-not-allowed disabled:opacity-60 lg:h-[60px] lg:rounded-[16px] lg:text-[22px]"
                    disabled={isAutoLoggingIn || isLoginSubmitting}
                  >
                    <span>
                      {isAutoLoggingIn || isLoginSubmitting
                        ? "Loading..."
                        : "Login"}
                    </span>
                    <ArrowRight className="h-5 w-5 lg:h-[26px] lg:w-[26px]" />
                  </button>

                  <div className="text-center text-[14px] text-[#6B7280] lg:text-[16px]">
                    <span>Dont you have account ? </span>
                    <button
                      type="button"
                      onClick={() =>
                        window.open(
                          "https://riyatechmatrimony.com/creatematrimonyapp/website",
                          "_blank",
                        )
                      }
                      className="font-semibold text-[#DB2777] transition hover:text-[#6D28D9]"
                      disabled={isAutoLoggingIn || isLoginSubmitting}
                    >
                      Create now
                    </button>
                  </div>
                </form>
              ) : (
                <form
                  onSubmit={handleUserLoginContinue}
                  className="mt-5 space-y-4 lg:mt-7 lg:space-y-[22px]"
                >
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedRole(null);
                      setUserLoginHint("");
                      setError("");
                    }}
                    className="text-sm font-semibold text-[#6D28D9] transition hover:text-[#DB2777]"
                  >
                    Back
                  </button>
                  <div>
                    <label
                      htmlFor="userLoginId"
                      className="mb-1.5 block text-[14px] font-bold text-[#374151] lg:mb-2 lg:text-[16px]"
                    >
                      Mobile Number / Marital ID
                    </label>
                    <div className="flex h-[52px] items-center rounded-xl border border-[#E5E7EB] bg-white px-4 shadow-sm transition focus-within:border-[#6D28D9] focus-within:ring-4 focus-within:ring-violet-100 lg:h-[60px] lg:rounded-[14px]">
                      <UserCheck className="h-5 w-5 shrink-0 text-[#6B7280] lg:h-[22px] lg:w-[22px]" />
                      <input
                        type="text"
                        id="userLoginId"
                        value={userLoginId}
                        onChange={(e) => {
                          setUserLoginId(e.target.value);
                          setUserLoginHint("");
                        }}
                        className="h-full w-full border-0 bg-transparent px-3 text-[15px] text-[#1F2937] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-0 lg:text-[18px]"
                        placeholder="Enter mobile number or marital ID"
                        disabled={isUserLookupSubmitting}
                      />
                    </div>
                  </div>

                  <div className="relative">
                    <label
                      htmlFor="userLoginPassword"
                      className="mb-1.5 block text-[14px] font-bold text-[#374151] lg:mb-2 lg:text-[16px]"
                    >
                      Password
                    </label>
                    <div className="flex h-[52px] items-center rounded-xl border border-[#E5E7EB] bg-white px-4 shadow-sm transition focus-within:border-[#6D28D9] focus-within:ring-4 focus-within:ring-violet-100 lg:h-[60px] lg:rounded-[14px]">
                      <Lock className="h-5 w-5 shrink-0 text-[#9CA3AF] lg:h-[22px] lg:w-[22px]" />
                      <input
                        type={showUserPassword ? "text" : "password"}
                        id="userLoginPassword"
                        value={userLoginPassword}
                        onChange={(e) => {
                          setUserLoginPassword(e.target.value);
                          setUserLoginHint("");
                        }}
                        className="h-full w-full border-0 bg-transparent px-3 pr-2 text-[15px] text-[#1F2937] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-0 lg:text-[18px]"
                        placeholder="Enter your password"
                        disabled={isUserLookupSubmitting}
                      />
                      <button
                        type="button"
                        onClick={toggleUserPasswordVisibility}
                        className="text-[#6B7280] transition hover:text-[#6D28D9]"
                        disabled={isUserLookupSubmitting}
                      >
                        {showUserPassword ? (
                          <EyeOff className="h-5 w-5 lg:h-[22px] lg:w-[22px]" />
                        ) : (
                          <Eye className="h-5 w-5 lg:h-[22px] lg:w-[22px]" />
                        )}
                      </button>
                    </div>
                  </div>

                  {userLoginHint && (
                    <div className="rounded-2xl border border-violet-200 bg-violet-50 px-4 py-3 text-sm font-medium text-violet-700">
                      {userLoginHint}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="flex h-[52px] w-full items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-[#6D28D9] via-[#9333EA] to-[#DB2777] px-5 text-[18px] font-extrabold text-white shadow-[0_18px_40px_rgba(109,40,217,0.34)] transition hover:translate-y-[-1px] hover:shadow-[0_22px_50px_rgba(109,40,217,0.40)] lg:h-[60px] lg:rounded-[16px] lg:text-[22px]"
                    disabled={isUserLookupSubmitting}
                  >
                    <span>
                      {isUserLookupSubmitting ? "Checking..." : "Login"}
                    </span>
                    <ArrowRight className="h-5 w-5 lg:h-[26px] lg:w-[26px]" />
                  </button>

                  <div className="text-center text-[14px] text-[#6B7280] lg:text-[16px]">
                    <span>Dont you have account ? </span>
                    <button
                      type="button"
                      onClick={() => navigate("/profile/register")}
                      className="font-semibold text-[#DB2777] transition hover:text-[#6D28D9]"
                      disabled={isUserLookupSubmitting}
                    >
                      Create now
                    </button>
                  </div>
                </form>
              )}

              {selectedRole !== null && (
                <>
                  <div className="mb-3 mt-4 flex items-center justify-center gap-4 py-1 lg:mb-[10px] lg:mt-[26px]">
                    <div className="h-px flex-1 bg-slate-200" />
                    <span className="text-[12px] lowercase tracking-[0.08em] text-[#CBD5E1]">
                      or
                    </span>
                    <div className="h-px flex-1 bg-slate-200" />
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      if (activeTab === "user") {
                        navigate("/profile/register");
                        return;
                      }

                      window.open(
                        "https://riyatechmatrimony.com/creatematrimonyapp/website",
                        "_blank",
                      );
                    }}
                    className="flex w-full items-center justify-between rounded-[18px] border border-[#FBCFE8] bg-gradient-to-r from-[#FFF1F7] to-white p-4 text-left shadow-sm transition hover:border-pink-300 hover:shadow-md lg:min-h-[96px] lg:px-5 lg:py-5"
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-pink-100 text-[#DB2777] lg:h-16 lg:w-16 lg:rounded-[18px]">
                        <CheckCircle2 className="h-5 w-5 lg:h-7 lg:w-7" />
                      </div>
                      <div>
                        <p className="text-[13px] font-medium text-[#6B7280] lg:text-[16px]">
                          {activeTab === "user" ? (
                            <>
                              <span className="lg:hidden">
                                {userSignupPrompt}
                              </span>
                              <span className="hidden lg:inline">
                                Need a new account?
                              </span>
                            </>
                          ) : (
                            <>
                              <span className="lg:hidden">
                                {bureauSignupPrompt}
                              </span>
                              <span className="hidden lg:inline">
                                Are you a new member?
                              </span>
                            </>
                          )}
                        </p>
                        <p className="text-[20px] font-extrabold leading-tight text-[#6D28D9] lg:text-[23px] lg:leading-[1.05]">
                          {activeTab === "user" ? (
                            <>
                              <span className="lg:hidden">{userSignupCta}</span>
                              <span className="hidden lg:inline">
                                Create Your Profile
                              </span>
                            </>
                          ) : (
                            <>
                              <span className="lg:hidden">
                                {bureauSignupCta}
                              </span>
                              <span className="hidden lg:inline">
                                Create now
                              </span>
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                    <ArrowRight className="h-6 w-6 shrink-0 text-[#DB2777] lg:h-[30px] lg:w-[30px]" />
                  </button>
                </>
              )}
            </div>
          </section>
        </div>
      </div>

      {isForgotModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 px-4 backdrop-blur-sm">
          <div
            data-scroll-container="true"
            className="relative max-h-[calc(100dvh-2rem)] w-full max-w-md overflow-y-auto rounded-[2rem] border border-white/60 bg-white p-6 shadow-[0_28px_80px_rgba(15,23,42,0.28)]"
          >
            <button
              type="button"
              onClick={closeForgotPasswordModal}
              className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition hover:bg-slate-200 hover:text-slate-700"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-fuchsia-100 via-white to-violet-100 text-violet-700 shadow-sm">
              {forgotStep === "request" ? (
                <Mail className="h-6 w-6" />
              ) : (
                <Lock className="h-6 w-6" />
              )}
            </div>
            <h3 className="text-3xl font-semibold text-slate-800">
              {forgotStep === "request" ? "Forgot Password" : "Reset Password"}
            </h3>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              {forgotStep === "request"
                ? "Enter your registered email address and we'll send you an OTP to reset your password."
                : "Enter the OTP you received along with your new password."}
            </p>

            {forgotStatus && (
              <div
                className={`mt-4 rounded-2xl border px-4 py-3 text-sm ${
                  forgotStatus.type === "success"
                    ? "border-green-200 bg-green-50 text-green-700"
                    : "border-red-200 bg-red-50 text-red-600"
                }`}
              >
                {forgotStatus.text}
              </div>
            )}

            <form
              className="mt-6 space-y-4"
              onFocusCapture={(e) => revealFocusedField(e.target)}
              onSubmit={
                forgotStep === "request"
                  ? handleForgotPasswordSubmit
                  : handleResetPasswordSubmit
              }
            >
              <div>
                <label
                  htmlFor="forgotEmail"
                  className="block text-sm font-semibold text-slate-700"
                >
                  Registered Email
                </label>
                <input
                  type="email"
                  id="forgotEmail"
                  value={forgotForm.email}
                  onChange={(e) =>
                    handleForgotInputChange("email", e.target.value)
                  }
                  className="mt-2 w-full rounded-2xl border border-slate-200 p-3.5 focus:outline-none focus:ring-2 focus:ring-violet-500"
                  placeholder="name@email.com"
                  disabled={
                    forgotStep === "reset" ||
                    isForgotSubmitting ||
                    isResetSubmitting
                  }
                  required
                />
              </div>

              {forgotStep === "reset" && (
                <>
                  <div>
                    <label
                      htmlFor="otp"
                      className="block text-sm font-semibold text-slate-700"
                    >
                      OTP
                    </label>
                    <input
                      type="text"
                      id="otp"
                      value={forgotForm.otp}
                      onChange={(e) =>
                        handleForgotInputChange("otp", e.target.value)
                      }
                      className="mt-2 w-full rounded-2xl border border-slate-200 p-3.5 focus:outline-none focus:ring-2 focus:ring-violet-500"
                      placeholder="Enter the OTP"
                      required
                      disabled={isResetSubmitting}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="newPassword"
                      className="block text-sm font-semibold text-slate-700"
                    >
                      New Password
                    </label>
                    <input
                      type="password"
                      id="newPassword"
                      value={forgotForm.newPassword}
                      onChange={(e) =>
                        handleForgotInputChange("newPassword", e.target.value)
                      }
                      className="mt-2 w-full rounded-2xl border border-slate-200 p-3.5 focus:outline-none focus:ring-2 focus:ring-violet-500"
                      placeholder="Enter new password"
                      required
                      disabled={isResetSubmitting}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="confirmNewPassword"
                      className="block text-sm font-semibold text-slate-700"
                    >
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      id="confirmNewPassword"
                      value={forgotForm.confirmPassword}
                      onChange={(e) =>
                        handleForgotInputChange(
                          "confirmPassword",
                          e.target.value,
                        )
                      }
                      className="mt-2 w-full rounded-2xl border border-slate-200 p-3.5 focus:outline-none focus:ring-2 focus:ring-violet-500"
                      placeholder="Confirm new password"
                      required
                      disabled={isResetSubmitting}
                    />
                  </div>
                </>
              )}

              <button
                type="submit"
                className="w-full rounded-2xl bg-gradient-to-r from-violet-700 to-fuchsia-600 py-3.5 text-white transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
                disabled={
                  (forgotStep === "request" && isForgotSubmitting) ||
                  (forgotStep === "reset" && isResetSubmitting)
                }
              >
                {forgotStep === "request"
                  ? isForgotSubmitting
                    ? "Sending OTP..."
                    : "Send OTP"
                  : isResetSubmitting
                    ? "Resetting Password..."
                    : "Reset Password"}
              </button>
            </form>

            {forgotStep === "reset" && (
              <button
                type="button"
                onClick={() => {
                  if (isResetSubmitting) return;
                  setForgotStep("request");
                  setForgotStatus(null);
                  setForgotForm((prev) => ({
                    ...prev,
                    otp: "",
                    newPassword: "",
                    confirmPassword: "",
                  }));
                }}
                className="mt-4 w-full rounded-2xl border border-violet-100 bg-violet-50 py-2.5 text-sm font-medium text-violet-700 transition hover:bg-violet-100"
              >
                Use a different email
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default BureauLogin;
